import { useState } from 'react';
import { Loader } from "@/components/ui/loader";
import { useEnhancedAuthContext } from '@/context/auth/EnhancedAuthContext';
import { User, Target, Phone, UserPlus, BarChart3, Calendar, RefreshCw, LayoutDashboard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useUserDashboardData } from '@/hooks/useUserDashboardData';
import { useNavigate } from 'react-router-dom';
import ActivityFeed from '@/components/dashboard/ActivityFeed';
import TodaysSchedule from '@/components/dashboard/TodaysSchedule';
import UserPerformanceCards from '@/components/dashboard/UserPerformanceCards';
import PipelineFunnel from '@/components/dashboard/PipelineFunnel';
import AttentionRequired from '@/components/dashboard/AttentionRequired';
import ActivityRecencyBar from '@/components/dashboard/ActivityRecencyBar';
import { TopCountriesList } from '@/components/leads/analytics/TopCountriesList';

const UserDashboard = () => {
  const { user } = useEnhancedAuthContext();
  const navigate = useNavigate();
  const { 
    stats, 
    todaysSchedule, 
    activities, 
    upcomingCalls, 
    leadActivityStats,
    leadStats,
    isLoading, 
    error,
    refetchData
  } = useUserDashboardData();


  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="mb-6 flex items-center gap-2">
          <LayoutDashboard className="h-5 w-5 text-muted-foreground" />
          <p className="text-muted-foreground">Your personal productivity center</p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader size={32} text="Loading your dashboard..." />
        </div>
      </div>
    );
  }

  const getRoleDisplayName = (role: string) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'manager': return 'Manager';
      case 'user': return 'Sales Agent';
      default: return role;
    }
  };

  return (
    <div className="container mx-auto py-4 md:py-6 space-y-4 md:space-y-6 px-4 md:px-6">
      {/* Personal Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3">
            <LayoutDashboard className="h-5 w-5 md:h-6 md:w-6 text-muted-foreground" />
            <p className="text-sm md:text-base text-muted-foreground">
              Your personal productivity center
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={refetchData}
            disabled={isLoading}
            className="h-8 w-8 md:h-10 md:w-10"
          >
            <RefreshCw className={`h-4 w-4 md:h-5 md:w-5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Performance Stats with Real Data */}
        <UserPerformanceCards
          totalActivities={stats.totalActivities}
          todaysActivities={stats.todaysActivities}
          callsAttempted={stats.callsAttempted}
          callsReached={stats.callsReached}
          emailsSent={stats.emailsSent}
          smsSent={stats.smsSent}
          leadsContacted={stats.leadsContacted}
          leadsCreated={stats.leadsCreated}
          leadsAssigned={stats.leadsAssigned}
          callSuccessRate={stats.callSuccessRate}
          conversionRate={stats.conversionRate}
          tasksCompletedCount={stats.tasksCompletedCount}
          tasksRemainingCount={stats.tasksRemainingCount}
          totalTasksCount={stats.totalTasksCount}
          todaysTasksCount={stats.todaysTasksCount}
          todaysCompletedTasksCount={stats.todaysCompletedTasksCount}
          todaysRemainingTasksCount={stats.todaysRemainingTasksCount}
          totalEventsCount={stats.totalEventsCount}
          todaysEventsCount={stats.todaysEventsCount}
          todaysCompletedEventsCount={stats.todaysCompletedEventsCount}
          todaysRemainingEventsCount={stats.todaysRemainingEventsCount}
        />

      {/* Pipeline & Attention Required */}
      {leadActivityStats && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <PipelineFunnel
              pipelineStages={leadActivityStats.pipeline_stages}
              total={leadActivityStats.total}
            />
            <AttentionRequired
              staleLeads={leadActivityStats.stale_leads}
              neverContacted={leadActivityStats.never_contacted}
              staleNoPhone={leadActivityStats.stale_no_phone}
              staleNoEmail={leadActivityStats.stale_no_email}
              staleDaysThreshold={leadActivityStats.stale_days_threshold}
            />
          </div>

          <ActivityRecencyBar recency={leadActivityStats.activity_recency} />
        </>
      )}

      {/* Top Countries - Full Width */}
      <TopCountriesList countries={leadStats?.countries} />

      {/* Today's Schedule */}
      <TodaysSchedule 
        events={todaysSchedule.events}
        tasks={todaysSchedule.tasks}
        upcomingCalls={upcomingCalls}
        onEventClick={(event) => {
          navigate('/app/schedule');
        }}
        onTaskComplete={(task) => {
          console.log('Completing task:', task.id);
        }}
        onAddEvent={() => {
          navigate('/app/schedule');
        }}
        onCallJoin={(call) => {
          navigate('/app/calls');
        }}
      />

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg md:text-xl">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <Button 
              className="h-16 md:h-20 flex-col gap-1 md:gap-2 text-xs md:text-sm"
              onClick={() => navigate('/app/calls')}
            >
              <Phone className="h-4 w-4 md:h-6 md:w-6" />
              <span>Start Call</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-16 md:h-20 flex-col gap-1 md:gap-2 text-xs md:text-sm"
              onClick={() => navigate('/app/leads')}
            >
              <UserPlus className="h-4 w-4 md:h-6 md:w-6" />
              <span>Add Lead</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-16 md:h-20 flex-col gap-1 md:gap-2 text-xs md:text-sm"
              onClick={() => navigate('/app/schedule')}
            >
              <Calendar className="h-4 w-4 md:h-6 md:w-6" />
              <span className="hidden sm:inline">View Schedule</span>
              <span className="sm:hidden">Schedule</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-16 md:h-20 flex-col gap-1 md:gap-2 text-xs md:text-sm"
              onClick={() => navigate('/app/performance')}
            >
              <BarChart3 className="h-4 w-4 md:h-6 md:w-6" />
              <span className="hidden sm:inline">View Reports</span>
              <span className="sm:hidden">Reports</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Real Activity Feed - Full Width */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          {error && (
            <p className="text-xs text-muted-foreground">Using sample data due to connection issues</p>
          )}
        </CardHeader>
        <CardContent>
          <ActivityFeed items={activities} />
        </CardContent>
      </Card>
    </div>
  );
};

export default UserDashboard;